import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import Loader from '../../../../../../components/loader/Loader';
import { api as coursesApi } from '../../../../../../api/courses';
import CenteredModal from '../../../../../../components/Modal/CenteredModal';

const formatDuration = (value: any) => {
  if (!value && value !== 0) return '00:00';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') {
    return `${String(value).padStart(2, '0')}:00`;
  }
  return '00:00';
};

const normalizedType = (value?: string) => {
  if (!value) return 'video';
  return value.toLowerCase().includes('ebook') ? 'ebook' : 'video';
};

const ModuleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const activeModule = JSON.parse(sessionStorage.getItem('active-module') || '{}');
  const [openPreview, setOpenPreview] = useState<boolean>(false);
  const [activeContent, setActiveContent] = useState<any>(null);

  const { data, isLoading } = useQuery(
    ['module-detail', id],
    () => coursesApi.getIndividualModule(id),
    {
      enabled: !!id,
      cacheTime: 100,
      staleTime: 100,
      refetchOnWindowFocus: true,
      select: (response: any) => response?.data || response,
    }
  );

  const moduleData = data || activeModule;

  const contentItems = useMemo(() => {
    const resources = moduleData?.resources || moduleData?.moduleResources || [];
    if (resources?.length) return resources;

    return (moduleData?.languages || [])
      .filter((lang: any) => !!lang?.filePath)
      .map((lang: any, index: number) => ({
        id: `${lang?.symbol || 'lang'}-${index}`,
        title: `${lang?.language || lang?.symbol || 'Language'} content`,
        type: moduleData?.type || 'resource',
        filePath: lang?.filePath,
      }));
  }, [moduleData]);

  const selectedType = normalizedType(activeContent?.type || moduleData?.type);
  const selectedDuration = formatDuration(activeContent?.duration || moduleData?.duration);
  const selectedPath = activeContent?.filePath || moduleData?.filePath || 'No file path available';

  if (isLoading) return <Loader />;

  return (
    <div className="p-5">
      <button
        onClick={() => navigate('/admin/modules')}
        className="flex items-center text-gray-500 hover:text-gray-700 mb-6 font-semibold"
      >
        <i className="bi bi-chevron-left mr-2" /> Back to Modules
      </button>

      <div className="bg-[#0A3059] rounded-lg text-white p-6 mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
            moduleData?.status === 'published' ? 'bg-[#D1F2D1] text-[#00984C]' : 'bg-[#FFE8CC] text-[#FF9100]'
          }`}>
            {moduleData?.status || 'Unknown'}
          </span>
        </div>
        <h1 className="text-2xl font-bold">{moduleData?.name || 'Module'}</h1>

        <div className="bg-white text-black mt-6 rounded-lg p-6 grid grid-cols-1 md:grid-cols-4 gap-4 shadow-sm">
          <div>
            <p className="text-gray-500 text-sm mb-1">Assessment</p>
            <p className="font-semibold">{moduleData?.isAssessment ? 'Yes' : 'No'}</p>
          </div>
          <div>
            <p className="text-gray-500 text-sm mb-1">Assessment Type</p>
            <p className="font-semibold capitalize">{moduleData?.assessmentType || 'None'}</p>
          </div>
          <div>
            <p className="text-gray-500 text-sm mb-1">Type</p>
            <p className="font-semibold capitalize">{moduleData?.type || 'N/A'}</p>
          </div>
          <div>
            <p className="text-gray-500 text-sm mb-1">Languages</p>
            <p className="font-semibold">{moduleData?.languages?.length || 1}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
        <div className="flex justify-between items-center border-b border-gray-100 pb-4 mb-4">
          <h3 className="font-semibold text-gray-800">Assigned Content ({contentItems?.length || 0})</h3>
        </div>

        <div className="space-y-4">
          {contentItems?.map((item: any, index: number) => (
            <div key={item?.id || index} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
              <div className="flex items-center space-x-4">
                <span className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-semibold text-sm">
                  {index + 1}
                </span>
                <div>
                  <h4 className="font-semibold text-gray-800">{item?.title || item?.name || 'Module Content'}</h4>
                  <p className="text-xs text-gray-500 capitalize">{normalizedType(item?.type || moduleData?.type)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  className="text-sm text-primary-blue underline"
                  onClick={() => {
                    setActiveContent(item);
                    setOpenPreview(true);
                  }}
                >
                  Preview
                </button>
                {item?.filePath && (
                  <a href={item?.filePath} target="_blank" rel="noreferrer" className="text-sm text-primary-blue underline">
                    Open
                  </a>
                )}
              </div>
            </div>
          ))}
          {contentItems?.length === 0 && (
            <p className="text-center text-gray-500 py-4">No content found for this module.</p>
          )}
        </div>
      </div>

      <CenteredModal
        title={<h5 className='text-md font-semibold'>Preview: {activeContent?.title || activeContent?.name || moduleData?.name || 'Module Content'}</h5>}
        bgColor={'bg-white'}
        open={openPreview}
        setOpen={setOpenPreview}
        style={{ padding: 0 }}
        width={'w-[620px]'}
        closeButtonLabel={'Close'}
        btnBgColor={'bg-primary-blue'}
        onClose={() => setOpenPreview(false)}
        headerClassName={'text-sm flex items-center justify-between leading-6 font-medium text-customBlue text-left'}
      >
        <section className='px-4 pb-4 pt-2'>
          <div className='rounded-xl bg-[#091A44] h-[260px] text-white flex items-center justify-center text-center p-4'>
            <div>
              <div className='w-16 h-16 mx-auto mb-4 rounded-full bg-white/20 flex items-center justify-center'>
                <span className={`text-2xl ${selectedType === 'ebook' ? 'bi bi-journal-richtext' : 'bi bi-camera-video'}`}></span>
              </div>
              <h6 className='text-lg font-semibold'>
                {selectedType === 'ebook' ? 'E-book reader placeholder' : 'Video player placeholder'}
              </h6>
              <p className='text-sm text-gray-300 mt-2 break-all'>
                {selectedPath}
              </p>
            </div>
          </div>

          <div className='grid grid-cols-2 gap-3 mt-3'>
            <div className='bg-primaryGray rounded-md p-3'>
              <p className='text-sm text-gray-500'>Duration</p>
              <p className='font-semibold text-lg'>{selectedDuration}</p>
            </div>
            <div className='bg-primaryGray rounded-md p-3'>
              <p className='text-sm text-gray-500'>Type</p>
              <p className='font-semibold text-lg capitalize'>{selectedType}</p>
            </div>
          </div>
        </section>
      </CenteredModal>
    </div>
  );
};

export default ModuleDetail;
